import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import ResizableHeader from "@/components/ResizableHeader";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, User, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { blogPosts } from "@/data/blog-posts";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const post = blogPosts.find(
    (p) => p.slug === slug || p.id.toString() === slug
  );

  if (!post) {
    return (
      <div className="min-h-screen">
        <ResizableHeader />
        <main className="container px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-3xl font-bold mb-4">Post Not Found</h1>
            <p className="text-muted-foreground mb-8">
              The blog post you're looking for doesn't exist.
            </p>
            <Button onClick={() => navigate("/blog")}>Back to Blog</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const canonicalUrl = `https://www.bankconvert.org/blog/${post.slug}`;
  const metaTitle = (post as any).metaTitle || `${post.title} | BankConvert`;
  const metaDescription = (post as any).metaDescription || post.description || post.excerpt;

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`https://www.bankconvert.org${post.image}`} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={`https://www.bankconvert.org${post.image}`} />
      </Helmet>
      <ResizableHeader />
      <main>
        {/* Hero Section */}
        <section className="py-16 bg-gradient-subtle">
          <div className="container px-4">
            <div className="max-w-4xl mx-auto">
              <Button
                variant="ghost"
                onClick={() => navigate("/blog")}
                className="mb-8"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Button>
              
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                    {post.category}
                  </span>
                  <span>•</span>
                  <span>{post.readTime}</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl font-bold">
                  {post.title}
                </h1>
                
                <p className="text-xl text-muted-foreground">
                  {post.description || post.excerpt}
                </p>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground pt-4">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{post.author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(post.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="container px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="bg-card shadow-lg">
                <CardContent className="p-8 md:p-12">
                  <article className="prose prose-slate dark:prose-invert max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        table: ({ node, ...props }) => (
                          <div className="overflow-x-auto my-6">
                            <table className="w-full border-collapse text-sm" {...props} />
                          </div>
                        ),
                        thead: ({ node, ...props }) => (
                          <thead className="bg-muted" {...props} />
                        ),
                        tbody: ({ node, ...props }) => (
                          <tbody {...props} />
                        ),
                        tr: ({ node, ...props }) => (
                          <tr className="border-b border-border" {...props} />
                        ),
                        th: ({ node, ...props }) => (
                          <th className="text-left font-semibold text-foreground px-4 py-3" {...props} />
                        ),
                        td: ({ node, ...props }) => (
                          <td className="text-muted-foreground px-4 py-3" {...props} />
                        ),
                        h2: ({ node, ...props }) => (
                          <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground" {...props} />
                        ),
                        h3: ({ node, ...props }) => (
                          <h3 className="text-xl font-semibold mt-6 mb-3 text-foreground" {...props} />
                        ),
                        p: ({ node, ...props }) => (
                          <p className="text-muted-foreground leading-relaxed mb-4" {...props} />
                        ),
                        ul: ({ node, ...props }) => (
                          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4" {...props} />
                        ),
                        ol: ({ node, ...props }) => (
                          <ol className="list-decimal list-inside space-y-2 text-muted-foreground mb-4" {...props} />
                        ),
                        li: ({ node, ...props }) => (
                          <li className="text-muted-foreground" {...props} />
                        ),
                        strong: ({ node, ...props }) => (
                          <strong className="text-foreground font-semibold" {...props} />
                        ),
                        a: ({ node, ...props }) => (
                          <a className="text-primary hover:underline" {...props} />
                        ),
                        blockquote: ({ node, ...props }) => (
                          <blockquote className="border-l-4 border-primary/50 pl-4 italic text-muted-foreground my-4" {...props} />
                        ),
                        code: ({ node, inline, ...props }: any) => {
                          if (inline) {
                            return (
                              <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground" {...props} />
                            );
                          }
                          return (
                            <code className="block bg-muted p-4 rounded-lg text-sm font-mono text-foreground overflow-x-auto my-4" {...props} />
                          );
                        },
                        hr: ({ node, ...props }) => (
                          <hr className="my-8 border-border" {...props} />
                        ),
                      }}
                    >
                      {post.content || post.excerpt}
                    </ReactMarkdown>
                  </article>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-10 bg-muted/30">
          <div className="container px-4 sm:px-6 max-w-3xl mx-auto">
            <h2 className="text-lg font-semibold mb-4">More Guides</h2>
            <ul className="space-y-2">
              <li>
                <a href="/blog/convert-pdf-bank-statement-to-excel" className="text-primary hover:underline">
                  How to convert PDF bank statements to Excel
                </a>
              </li>
              <li>
                <a href="/blog/csv-to-excel-converter-free-instant-private" className="text-primary hover:underline">
                  Free private CSV to Excel converter — no upload required
                </a>
              </li>
              <li>
                <a href="/blog/announcing-paid-plans-bank-statement-pdf-converter" className="text-primary hover:underline">
                  BankConvert paid plans — what's included
                </a>
              </li>
              <li>
                <a href="/blog/convert-bank-statements-to-excel-30-seconds" className="text-primary hover:underline">
                  Convert bank statements to Excel in 30 seconds
                </a>
              </li>
              <li>
                <a href="/blog" className="text-primary hover:underline">
                  All guides →
                </a>
              </li>
            </ul>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;

